import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { RealtimeMessage } from '@ridemaadi/types';
import { useAuth } from '../../../providers/AuthProvider';
import { locationApi } from '../location';
import { rideKeys, useMyRides } from '../hooks';
import { useRideCompletionCelebration } from './RideCompletionCelebrationProvider';

const MAX_SOCKETS = 5;

/** Realtime listener - shows celebration when a joined ride completes. */
export function RideCompletionNotificationListener() {
  const { user } = useAuth();
  const { data } = useMyRides({ enabled: Boolean(user) });
  const qc = useQueryClient();
  const { showForRideId } = useRideCompletionCelebration();
  const showRef = useRef(showForRideId);
  showRef.current = showForRideId;
  const prevStatusRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!data?.items) return;
    const prev = prevStatusRef.current;
    for (const ride of data.items) {
      const was = prev.get(ride.id);
      if (was === 'active' && ride.status === 'completed') {
        void showRef.current(ride.id);
      }
      prev.set(ride.id, ride.status);
    }
  }, [data?.items]);

  const trackedRideIds = useMemo(() => {
    const ids =
      data?.items
        .filter((r) => r.status === 'scheduled' || r.status === 'active' || r.status === 'completed')
        .slice(0, MAX_SOCKETS)
        .map((r) => r.id) ?? [];
    return ids.join(',');
  }, [data?.items]);

  useEffect(() => {
    if (!user || !trackedRideIds) return;

    const rideIds = trackedRideIds.split(',').filter(Boolean);
    let closed = false;
    const sockets: WebSocket[] = [];

    const onCompleted = (rideId: string) => {
      qc.invalidateQueries({ queryKey: rideKeys.detail(rideId) });
      qc.invalidateQueries({ queryKey: rideKeys.mine });
      void showRef.current(rideId);
    };

    const connectRide = async (rideId: string) => {
      try {
        const { url } = await locationApi.realtimeUrl(rideId);
        if (closed) return;
        const ws = new WebSocket(url);
        sockets.push(ws);

        ws.onmessage = (event) => {
          const msg = JSON.parse(event.data as string) as RealtimeMessage;
          if (msg.type === 'status_change' && msg.status === 'completed') {
            onCompleted(rideId);
          }
        };
      } catch {
        /* polling fallback via useMyRides */
      }
    };

    rideIds.forEach((id) => void connectRide(id));

    return () => {
      closed = true;
      sockets.forEach((ws) => ws.close());
    };
  }, [user, trackedRideIds, qc]);

  return null;
}
